import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

/**
 * Style factories for the collapsible filter panel that sits above the
 * DataGrid. Every entry is a `(theme: Theme) => SxProps<Theme>` function —
 * including the purely static ones — so callers have a single consistent
 * invocation pattern (`filterPanelStyles.icon(theme)`) and it stays easy
 * to introduce theme-dependent tweaks later without touching the call sites.
 *
 * Review refactor R8 — normalized to "always a function".
 */
type FilterPanelStyleFn = (theme: Theme) => SxProps<Theme>;

interface FilterPanelStyles {
	container: FilterPanelStyleFn;
	icon: FilterPanelStyleFn;
	iconContainer: FilterPanelStyleFn;
	toggleButton: FilterPanelStyleFn;
	toggleButtonIcon: FilterPanelStyleFn;
}

export const filterPanelStyles: FilterPanelStyles = {
	container: (theme: Theme) => ({
		backgroundColor: alpha(theme.palette.primary.main, 0.06),
		borderRadius: 1,
		border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
		padding: theme.spacing(1, 2),
		marginBottom: theme.spacing(1),
		transition: theme.transitions.create(["box-shadow", "border-color"], {
			duration: theme.transitions.duration.short,
		}),
		"&:hover": {
			borderColor: alpha(theme.palette.primary.main, 0.24),
		},
	}),
	icon: (theme: Theme) => ({
		color: theme.palette.primary.main,
		fontSize: 20,
	}),
	iconContainer: (_theme: Theme) => ({
		display: "flex",
		alignItems: "center",
		alignSelf: "center",
	}),
	toggleButton: (theme: Theme) => ({
		backgroundColor: alpha(theme.palette.primary.main, 0.12),
		border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
		"&:hover": {
			backgroundColor: alpha(theme.palette.primary.main, 0.2),
			borderColor: alpha(theme.palette.primary.main, 0.4),
		},
	}),
	toggleButtonIcon: (theme: Theme) => ({
		color: theme.palette.primary.main,
	}),
};
